import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTemplate } from "@hooks/useAppTemplate";
import useMixedItemDist from "@hooks/useMixedItemDist";
import {
	formatExamDate,
	formatExamDateStatValue,
	getCalendarDaysUntil,
} from "@memorang/helpers/date";
import {
	calculateOverallPointsPercentage,
	getAnsweredCount,
	getFallbackProficiencyLevelLabel,
	getProgressBand,
	groupQuestionTypesBySkill,
} from "@memorang/helpers/language-learning";
import {
	QUESTION_TYPE_MASTERY_WINDOW,
	SKILL_MASTERY_WINDOW,
	resolveMasteryForTagId,
} from "@memorang/helpers/language-learning-mastery";
import { isWeb } from "@memorang/helpers/platform";
import {
	compareTagSortOrder,
	isProficiencyTag,
	isQuestionTypeTag,
	isSkillTag,
} from "@memorang/helpers/tag";
import type {
	MixedItemDist,
	MixedItemTag,
	PracticeTestExam,
	RecommendationContext,
} from "@memorang/types";
import type { LanguageLearningSkillSection } from "@memorang/ui/components/LanguageLearningProgressApplet";
import type { LanguageLearningProgressSkeletonProps } from "@memorang/ui/components/LanguageLearningProgressApplet/LanguageLearningProgressSkeleton";
import type { OverviewStat } from "@memorang/ui/components/ProgressOverview";
import type { ItemTypeProgressRow } from "@memorang/ui/components/SkillProgressCards/ItemTypesCard";
import type { StatTileCta } from "@memorang/ui/components/StatTile";
import {
	buildProficiencyIconMap,
	getProficiencyLevelIcons,
	getQuestionTypeIcon,
	getSkillIcon,
} from "@memorang/ui/constants";
import type { LevelIconName } from "@memorang/ui/icon";
import { useQuery } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	LANGUAGE_LEARNING_PROGRESS_OVERALL_USE_CASE,
	LANGUAGE_LEARNING_PROGRESS_SKILLS_USE_CASE,
	LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE,
	type ProgressSkillsTip,
	type SkillBandFills,
	buildLanguageLearningTipDataKey,
	buildProgressOverallTipData,
	buildSkillsTipData,
	buildTipSurfaceState,
	createProgressSkillsTipSchema,
	filterSummariesToKeys,
	getAttemptedSkillKeys,
	getCanonicalSkillKey,
	progressOverallTipSchema,
	withTipAppFraming,
} from "../../tips/language-learning";
import { tipQueryOptions } from "../../tips/tipQueryOptions";
import {
	buildProficiencyContent,
	resolveProficiencyVariant,
} from "../helpers/proficiency-content";
import { selectRecommendedTestsForSkill } from "../helpers/recommended-tests";
import {
	buildSkillCta,
	getSkillCtaKind,
	hasSkillLevelEvidence,
} from "../helpers/skill-cta";
import {
	hasLikelyPracticeRecommendation,
	useHydratedStudyRecommendations,
} from "../recommendations/hooks/useHydratedStudyRecommendations";
import { useParsedRecommendationContext } from "./useParsedRecommendationContext";

type SkillLevels = Map<MixedItemTag["id"], string | undefined>;
type QuestionTypeMastery = Map<
	MixedItemTag["id"],
	ReturnType<typeof resolveMasteryForTagId>
>;

const EMPTY_MASTERY: ReturnType<typeof resolveMasteryForTagId> = {
	fills: [],
	estimatedLevel: undefined,
	displayLevel: undefined,
};

const buildOverviewStats = (
	skillTags: MixedItemTag[],
	skillDisplayLevels: SkillLevels,
	skillCtas: Map<MixedItemTag["id"], StatTileCta | undefined>,
	recsLoading: boolean,
	examDate: string | undefined,
	itemsAnswered: number,
	totalResponses: number | undefined,
	t: TFunction,
): OverviewStat[] => [
	...skillTags.map((skill) => {
		const displayLevel = skillDisplayLevels.get(skill.id);
		const cta = skillCtas.get(skill.id);
		return {
			id: `skill-${skill.id}`,
			icon: getSkillIcon(skill.title),
			value: displayLevel ?? "",
			label: skill.title,
			skillId: String(skill.id),
			cta,
			isLoading: recsLoading,
			tooltip:
				cta || !displayLevel
					? undefined
					: t("dashboard.stats.skillLevelTooltip", {
							skill: skill.title,
							level: displayLevel,
						}),
		} satisfies OverviewStat;
	}),
	...(examDate !== undefined
		? [
				{
					id: "days-until-exam",
					icon: "calendar-month-outline",
					value: formatExamDateStatValue(examDate) ?? "",
					label: t("progress.languageLearning.stats.untilExam"),
					tooltip: t("dashboard.stats.examDateTooltip", {
						date: formatExamDate(examDate) ?? "",
					}),
				} satisfies OverviewStat,
			]
		: []),
	totalResponses !== undefined
		? {
				id: "items-answered",
				icon: "content-copy",
				value: String(totalResponses),
				label: t("progress.languageLearning.stats.responsesAnswered"),
				tooltip: t("dashboard.stats.responsesAnsweredTooltip"),
			}
		: {
				id: "items-answered",
				icon: "card-multiple-outline",
				value: String(itemsAnswered),
				label: t("progress.languageLearning.stats.itemsAnswered"),
				tooltip: t("dashboard.stats.itemsAnsweredTooltip"),
			},
];

const buildItemTypeSubtitle = (
	questionType: MixedItemTag,
	hideTotalItemCounts: boolean,
	t: TFunction,
): string => {
	if (hideTotalItemCounts) {
		return t("dashboard.itemTypes.cardSubtitleResponses", {
			count: questionType.totalResponses,
		});
	}
	const attempted = getAnsweredCount(questionType);
	const total = questionType.numItems ?? (questionType.items ?? []).length;
	return total > 0 && attempted === total
		? t("dashboard.itemTypes.cardSubtitleComplete")
		: t("dashboard.itemTypes.cardSubtitlePartial", { attempted, total });
};

const buildItemTypeRows = (
	skillTitle: string,
	questionTypes: MixedItemTag[],
	questionTypeMastery: QuestionTypeMastery,
	proficiencyIconMap: Record<string, LevelIconName>,
	hideTotalItemCounts: boolean,
	t: TFunction,
): ItemTypeProgressRow[] =>
	questionTypes.map((questionType) => {
		const subtitle = buildItemTypeSubtitle(
			questionType,
			hideTotalItemCounts,
			t,
		);
		const { displayLevel: level } =
			questionTypeMastery.get(questionType.id) ?? EMPTY_MASTERY;
		return {
			id: String(questionType.id),
			icon: getQuestionTypeIcon(questionType.title),
			title: questionType.title,
			subtitle,
			level,
			levelIcon: level ? proficiencyIconMap[level] : undefined,
			tooltip: level
				? t("progress.languageLearning.itemTypeLevelTooltip", {
						skill: skillTitle,
						level,
						itemType: questionType.title,
					})
				: undefined,
		};
	});

const resolveSkillTip = (
	skill: Pick<MixedItemTag, "title" | "code">,
	tip?: ProgressSkillsTip,
): { proficiency?: string; itemTypes?: string } => {
	const key = getCanonicalSkillKey(skill);
	return key ? (tip?.skills[key] ?? {}) : {};
};

type BuildSectionArgs = {
	skill: MixedItemTag;
	skillBandFills: SkillBandFills;
	displayLevel: string | undefined;
	rowsBySkill: Map<MixedItemTag["id"], MixedItemTag[]>;
	questionTypeMastery: QuestionTypeMastery;
	proficiencyTargetScore: number;
	levelIcons: LevelIconName[];
	proficiencyIconMap: Record<string, LevelIconName>;
	hideTotalItemCounts: boolean;
	showProficiencyLevels: boolean;
	exams?: MixedItemDist["exams"];
	startableTests?: PracticeTestExam[];
	skillsTip?: ProgressSkillsTip;
	tipLoading: boolean;
	noScoresGraphicUrl?: string;
	t: TFunction;
};

const buildSection = ({
	skill,
	skillBandFills,
	displayLevel,
	rowsBySkill,
	questionTypeMastery,
	proficiencyTargetScore,
	levelIcons,
	proficiencyIconMap,
	hideTotalItemCounts,
	showProficiencyLevels,
	exams,
	startableTests,
	skillsTip,
	tipLoading,
	noScoresGraphicUrl,
	t,
}: BuildSectionArgs): LanguageLearningSkillSection => {
	const { proficiency, itemTypes } = resolveSkillTip(skill, skillsTip);
	const answeredCount = hideTotalItemCounts
		? skill.totalResponses
		: getAnsweredCount(skill);
	const hasTipEvidence = getAnsweredCount(skill) > 0;
	const fills = skillBandFills.get(skill.id) ?? [];
	const { content, cardSubtitle } = buildProficiencyContent({
		skill,
		fills,
		level: displayLevel,
		levelIcons,
		showProficiencyLevels,
		proficiencyTargetScore,
		exams,
		canStartTest:
			selectRecommendedTestsForSkill({ exams: startableTests, skill }).length >
			0,
		noScoresGraphicUrl,
		t,
	});
	const itemTypeRows = buildItemTypeRows(
		skill.title,
		rowsBySkill.get(skill.id) ?? [],
		questionTypeMastery,
		proficiencyIconMap,
		hideTotalItemCounts,
		t,
	);
	return {
		id: String(skill.id),
		title: skill.title,
		icon: getSkillIcon(skill.title),
		subtitle:
			answeredCount > 0
				? t(
						hideTotalItemCounts
							? "progress.languageLearning.responsesSubtitle"
							: "progress.languageLearning.answeredSubtitle",
						{ count: answeredCount },
					)
				: t("progress.languageLearning.notAnswered"),
		proficiency: content,
		proficiencyCardSubtitle: cardSubtitle,
		proficiencyTip: proficiency,
		itemTypesTip: itemTypes,
		tipLoading: tipLoading && hasTipEvidence,
		itemTypeRows,
		hideItemTypes: answeredCount === 0 || itemTypeRows.length === 0,
	};
};

type BuildProgressTipStateArgs = {
	mixedItemDist?: MixedItemDist;
	skillTags: MixedItemTag[];
	itemsAnswered: number;
	isLanguageLearningApp: boolean;
	recommendationContext: RecommendationContext | undefined;
	appId?: string | number;
	template?: string;
	examId?: string | number;
	examDate?: string;
	daysUntilExam?: number;
	showExamCountdown: boolean;
	language: string;
};

const buildProgressTipState = ({
	mixedItemDist,
	skillTags,
	itemsAnswered,
	isLanguageLearningApp,
	recommendationContext,
	appId,
	template,
	examId,
	examDate,
	daysUntilExam,
	showExamCountdown,
	language,
}: BuildProgressTipStateArgs) => {
	const surface = buildTipSurfaceState({
		isLanguageLearningApp,
		mixedItemDist,
		skillTags,
		recommendationContext,
		appId,
		template,
		examId,
		language,
	});
	if (!surface.enabled) {
		return {
			tipsEnabled: false,
			tipData: undefined,
			skillsDataKey: undefined,
			overallDataKey: undefined,
			progressSkillsTipSchema: createProgressSkillsTipSchema([]),
		};
	}
	const examFraming =
		showExamCountdown && examDate && daysUntilExam !== undefined
			? { examDate, daysUntilExam }
			: undefined;
	const activeSkillKeys = getAttemptedSkillKeys(
		surface.proficiencySummaries,
		surface.recognizedSkillKeys,
	);
	return {
		tipsEnabled: true,
		tipData: {
			skills: withTipAppFraming(
				appId,
				template,
				buildSkillsTipData(
					surface.dist,
					filterSummariesToKeys(surface.proficiencySummaries, activeSkillKeys),
				),
			),
			overall: withTipAppFraming(
				appId,
				template,
				buildProgressOverallTipData(
					surface.dist,
					surface.proficiencySummaries,
					itemsAnswered,
					examFraming,
				),
			),
		},
		skillsDataKey: buildLanguageLearningTipDataKey(surface.cacheContext),
		overallDataKey: buildLanguageLearningTipDataKey({
			...surface.cacheContext,
			daysUntilExam: showExamCountdown ? daysUntilExam : undefined,
		}),
		progressSkillsTipSchema: createProgressSkillsTipSchema(activeSkillKeys),
	};
};

export const useLanguageLearningProgress = () => {
	const { data: commonDistribution, isLoading } = useMixedItemDist();
	const recommendationContext = useParsedRecommendationContext();
	// Scores come from the common distribution; startable tests live in the
	// practice-test distribution, which is a separate (deduped) query.
	const {
		data: practiceTestDistribution,
		isLoading: practiceTestsQueryLoading,
	} = useMixedItemDist({
		action: "GET_PRATICETEST_DISTRIBUTION",
	});
	const practiceTestEnabled = useAppStore(
		(state) => state.exam.practiceTest?.enabled,
	);
	const { firstName, userPublicMetadataId } = useAuthContext();
	const {
		currentExamContext: { examDate, examId },
	} = useExamContext();
	const proficiencyTargetScore = useAppStore(
		(state) => state.app.proficiencyTargetScore,
	);
	const proficiencyLevelsConfig = useAppStore(
		(state) => state.app.proficiencyLevels,
	);
	const hideTotalItemCounts = useAppStore(
		(state) => state.app.hideTotalItemCounts,
	);
	const showProficiencyEstimates = useAppStore(
		(state) => state.reportView?.options?.showProficiencyEstimates,
	);
	const noScoresGraphicUrl = useAppStore(
		(state) => state.app.progressView?.noScoresGraphicUrl,
	);
	const { t, i18n } = useTranslation();
	const { template, isLanguageLearningApp } = useAppTemplate();
	const appId = useAppStore((state) => state.app.id);

	const levelIcons = getProficiencyLevelIcons(proficiencyLevelsConfig);
	const proficiencyIconMap = buildProficiencyIconMap(proficiencyLevelsConfig);

	const {
		skillTags,
		proficiencyTags,
		rowsBySkill,
		skillBandFills,
		skillLevels,
		skillDisplayLevels,
		questionTypeMastery,
		itemsAnswered,
	} = useMemo(() => {
		const tags = commonDistribution?.tags ?? [];
		const skillTags = tags.filter(isSkillTag).sort(compareTagSortOrder);
		const proficiencyTags = tags.filter(isProficiencyTag);
		const questionTypeTags = tags.filter(isQuestionTypeTag);
		const fallbackLevelLabel =
			getFallbackProficiencyLevelLabel(proficiencyTags);
		const skillMasteryEntries = skillTags.map(
			(skill) =>
				[
					skill.id,
					resolveMasteryForTagId(
						recommendationContext,
						skill.id,
						SKILL_MASTERY_WINDOW,
						fallbackLevelLabel,
					),
				] as const,
		);
		const skillBandFills: SkillBandFills = new Map(
			skillMasteryEntries.map(([id, { fills }]) => [id, fills]),
		);
		const skillLevels: SkillLevels = new Map(
			skillMasteryEntries.map(([id, { estimatedLevel }]) => [
				id,
				estimatedLevel,
			]),
		);
		const skillDisplayLevels: SkillLevels = new Map(
			skillMasteryEntries.map(([id, { displayLevel }]) => [id, displayLevel]),
		);
		// Resolved once per question type here rather than once per render inside
		// buildItemTypeRows — the mastery lookup rescans the recommendation
		// context's cells per progression level, which isn't cheap to repeat.
		const questionTypeMastery: QuestionTypeMastery = new Map(
			questionTypeTags.map(
				(questionType) =>
					[
						questionType.id,
						resolveMasteryForTagId(
							recommendationContext,
							questionType.id,
							QUESTION_TYPE_MASTERY_WINDOW,
							fallbackLevelLabel,
						),
					] as const,
			),
		);
		return {
			skillTags,
			proficiencyTags,
			rowsBySkill: groupQuestionTypesBySkill(skillTags, questionTypeTags),
			skillBandFills,
			skillLevels,
			skillDisplayLevels,
			questionTypeMastery,
			itemsAnswered: skillTags.reduce(
				(sum, skill) => sum + getAnsweredCount(skill),
				0,
			),
		};
	}, [commonDistribution, recommendationContext]);

	const daysUntilExam = getCalendarDaysUntil(examDate);
	const showExamCountdown = daysUntilExam !== undefined && daysUntilExam >= 0;

	const {
		tipsEnabled,
		tipData,
		skillsDataKey,
		overallDataKey,
		progressSkillsTipSchema,
	} = useMemo(
		() =>
			buildProgressTipState({
				mixedItemDist: commonDistribution,
				skillTags,
				itemsAnswered,
				isLanguageLearningApp,
				recommendationContext,
				appId,
				template,
				examId,
				examDate,
				daysUntilExam,
				showExamCountdown,
				language: i18n.language,
			}),
		[
			commonDistribution,
			skillTags,
			itemsAnswered,
			isLanguageLearningApp,
			recommendationContext,
			appId,
			template,
			examId,
			examDate,
			daysUntilExam,
			showExamCountdown,
			i18n.language,
		],
	);

	const skillsTip = useQuery(
		tipQueryOptions({
			useCase: LANGUAGE_LEARNING_PROGRESS_SKILLS_USE_CASE,
			data: tipData?.skills,
			dataKey: skillsDataKey,
			schema: progressSkillsTipSchema,
			userId: userPublicMetadataId,
			outputLanguage: i18n.language,
			additionalInstructions: LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE,
			enabled: tipsEnabled,
		}),
	);
	const overallTip = useQuery(
		tipQueryOptions({
			useCase: LANGUAGE_LEARNING_PROGRESS_OVERALL_USE_CASE,
			data: tipData?.overall,
			dataKey: overallDataKey,
			schema: progressOverallTipSchema,
			userId: userPublicMetadataId,
			outputLanguage: i18n.language,
			additionalInstructions: LANGUAGE_LEARNING_TIP_LEVEL_GUIDANCE,
			enabled: tipsEnabled,
		}),
	);

	const canStartPracticeTests = isWeb
		? Boolean(practiceTestEnabled?.web)
		: Boolean(practiceTestEnabled?.mobile);
	const startableTests = canStartPracticeTests
		? practiceTestDistribution?.exams
		: undefined;

	const hydrated = useHydratedStudyRecommendations({
		sessionCount: 3,
		durationMinutes: 10,
	});
	const recsLoading =
		practiceTestsQueryLoading || hydrated.status === "LOADING";

	const skillCtas = useMemo(
		() =>
			new Map(
				skillTags.map((skill) => [
					skill.id,
					Boolean(skillLevels.get(skill.id)) ||
					hasSkillLevelEvidence({ skill, exams: startableTests })
						? undefined
						: buildSkillCta(
								getSkillCtaKind({
									skill,
									startableTests,
									hasPracticeRecommendation: hasLikelyPracticeRecommendation(
										hydrated,
										[skill.id],
									),
								}),
								t,
							),
				]),
			),
		[skillTags, skillLevels, startableTests, hydrated, t],
	);

	const sections: LanguageLearningSkillSection[] = skillTags.map((skill) =>
		buildSection({
			skill,
			skillBandFills,
			displayLevel: skillDisplayLevels.get(skill.id),
			rowsBySkill,
			questionTypeMastery,
			proficiencyTargetScore,
			levelIcons,
			proficiencyIconMap,
			hideTotalItemCounts,
			showProficiencyLevels: Boolean(showProficiencyEstimates),
			exams: commonDistribution?.exams,
			startableTests,
			skillsTip: tipsEnabled ? skillsTip.data : undefined,
			tipLoading:
				tipsEnabled &&
				getCanonicalSkillKey(skill) !== undefined &&
				skillsTip.isLoading,
			noScoresGraphicUrl,
			t,
		}),
	);

	const totalResponses = hideTotalItemCounts
		? (commonDistribution?.totalResponses ?? 0)
		: undefined;

	const stats = buildOverviewStats(
		skillTags,
		skillDisplayLevels,
		skillCtas,
		recsLoading,
		examDate,
		itemsAnswered,
		totalResponses,
		t,
	);

	const overallPercent = calculateOverallPointsPercentage(proficiencyTags);
	const band = getProgressBand(overallPercent);
	const title = firstName
		? t(`progress.languageLearning.greeting.${band}`, { firstName })
		: t(`progress.languageLearning.greetingNoName.${band}`);
	const subtitle = showExamCountdown
		? t("progress.languageLearning.examCountdown", { count: daysUntilExam })
		: undefined;

	const skeletonProps: LanguageLearningProgressSkeletonProps = {
		proficiencyVariant: resolveProficiencyVariant({
			showProficiencyLevels: Boolean(showProficiencyEstimates),
			proficiencyTargetScore,
		}),
		showOverviewSubtitle: showExamCountdown,
	};

	return {
		isLoading,
		recsLoading,
		title,
		subtitle,
		stats,
		sections,
		skeletonProps,
		tip: tipsEnabled ? overallTip.data?.text : undefined,
		tipLoading: tipsEnabled && overallTip.isLoading,
		showEmptyState:
			totalResponses !== undefined ? totalResponses === 0 : itemsAnswered === 0,
	};
};
